import { useCallback, useEffect, useRef, useState } from 'react';
import { Page } from '../theme/elements/Page';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { DigitalBalanceRequestsTables } from 'sections/digitalBalanceRequests/DigitalBalanceRequestsTables';
import { getDigitalBalanceRequests } from 'services/balances.service';
import { getDigitalBalanceRequestsData, clearDigitalBalanceRequests } from 'state/reducers/balance.reducer';
import { Order } from 'components/table/types';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT_ORDER } from 'constants/pagination.constants';

const INITIAL_SORT_BY = 'createdAt';
const INITIAL_SORT_ORDER: Order = DEFAULT_SORT_ORDER;
const sortFieldMap: Record<string, string> = {
	createdAt: 'created_at',
	feezbackUpdatedAt: 'feezbackUpdatedAt',
	amount: 'amount',
	documentedOnHyperDate: 'hyperDocumentedAt',
	feezbackBankReferenceNumber: 'feezbackBankReferenceNumber',
};
const mapSortBy = (sortBy: string) => sortFieldMap[sortBy] || 'created_at';

export const DigitalBalanceRequestsApp = () => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);

	// Initial loading state for global loader
	const [isInitialLoading, setIsInitialLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const sortRef = useRef<{ sortBy: string; sortOrder: Order }>({
		sortBy: INITIAL_SORT_BY,
		sortOrder: INITIAL_SORT_ORDER,
	});

	const requests = useAppSelector((state) => getDigitalBalanceRequestsData(state, true));

	const fetchDocumentedRequests = useCallback(
		async (pageNum: number, sortOverride?: { sortBy: string; sortOrder: Order }) => {
			setIsLoading(true);
			setPage(pageNum);
			const sortToUse = sortOverride || sortRef.current;
			try {
				await dispatch(
					getDigitalBalanceRequests({
						documentedOnHyper: true,
						offset: pageNum * DEFAULT_PAGE_SIZE,
						limit: DEFAULT_PAGE_SIZE,
						sortBy: mapSortBy(sortToUse.sortBy),
						sortOrder: sortToUse.sortOrder,
					})
				);
			} finally {
				setIsLoading(false);
			}
		},
		[dispatch]
	);

	useEffect(() => {
		setPage(0);
		setIsInitialLoading(true);
		fetchDocumentedRequests(0).finally(() => setIsInitialLoading(false));
	}, [fetchDocumentedRequests]);

	useEffect(() => {
		return () => {
			dispatch(clearDigitalBalanceRequests());
		};
	}, [dispatch]);

	const handlePageChange = (pageNum: number) => fetchDocumentedRequests(pageNum);
	const handleSortChange = useCallback(
		(sortBy: string, sortOrder: Order) => {
			const nextSort = { sortBy, sortOrder };
			sortRef.current = nextSort;
			return fetchDocumentedRequests(0, nextSort);
		},
		[fetchDocumentedRequests]
	);

	return (
		<Page title={t('page.digitalBalanceRequests.title')} loading={isInitialLoading}>
			<DigitalBalanceRequestsTables
				requests={requests}
				page={page}
				isLoading={isLoading}
				onPageChange={handlePageChange}
				onSortChange={handleSortChange}
			/>
		</Page>
	);
};
